import { FundPerformanceOverview, FundsAndPerformanceExportExcelWrapper }
  from 'src/app/domain/organization/components/organization-overview/utils';
import { Rows } from '../../interfaces';
import { getYearOrSpace, formats, getCell, withCellStyles, dashInsteadOfNullCommon, spaceInsteadOfNull } from '../../utils';
import { boldLightGrayBgLeft, left, boldGrayBgLeftGrandTotal, centeredBoldGrayForHeaders, center, leftBold, leftIntend } from 'src/app/util/excelStyles';
import { FIRST_ROW_HEADERS_ORG_OVERVIEW, HEADERS_FOR_LOGIC_ORG_OVERVIEW } from './fundsAndPerfromanceTemplate';


export const getPreparedData = (wrapperData: FundsAndPerformanceExportExcelWrapper, headers: string[]): Rows => {
  const newData: Rows = [];
  const firstRow = FIRST_ROW_HEADERS_ORG_OVERVIEW;
  newData.push(firstRow);
  const designedHeaderRow = [];
  headers.forEach((header) => designedHeaderRow.push(withCellStyles(header, centeredBoldGrayForHeaders)));
  // pushing headers for display, headers includes one null value for white space
  newData.push(designedHeaderRow);
  const headersForLogic = HEADERS_FOR_LOGIC_ORG_OVERVIEW;
  // funds data
  const funds = wrapperData.funds.filter((f) => !f.isSpv);
  const spvs = wrapperData.funds.filter((f) => f.isSpv);
  shouldUseSubtotal = Boolean(funds.length && spvs.length);
  if (funds.length) {
    newData.push([withCellStyles('Funds', leftBold)]);
    currentIndexRow++;
    const fundsRows = getMainRows(funds, headersForLogic);
    newData.push(...fundsRows);
    const subTotalFunds = getSubTotalRow(headersForLogic, 4);
    currentIndexRow++;
    newData.push(...subTotalFunds);
  }
  // spv

  if (spvs.length) {
    newData.push([withCellStyles('SPVs', leftBold)]);
    currentIndexRow++;
    const spvsRows = getMainRows(spvs, headersForLogic);
    newData.push(...spvsRows);
    const subTotal = getSubTotalRow(headersForLogic, 5 + funds.length + 1);
    newData.push(...subTotal);
    currentIndexRow++;
  }
  if (shouldUseSubtotal) {
    const grandTotalRow = getGrandTotalRow(headersForLogic);
    newData.push(...grandTotalRow);
  }
  resetData();
  return newData;
};

const getMainRows = (data: FundPerformanceOverview[], headers: string[]): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = data.map((fp) => getMapping(fp));

  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of headers) {
      if (header === 'Vintage') {
        row.push(withCellStyles(mapped[header], center));
      } else if (header === 'Entity') {
        row.push(withCellStyles(mapped[header], leftIntend));
      } else {
        row.push(withCellStyles(mapped[header], left));
      }
    }
    rows.push(row);
  }
  return rows;
};

const getSubTotalRow = (headers: string[], startIndex: number): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getSubTotalMapping(startIndex);
  for (const mapped of mappedToHeaders) {
    const row = [];

    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldLightGrayBgLeft));
    }
    rows.push(row);
  }
  return rows;
};

const getGrandTotalRow = (headers: string[],): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getGrandTotalMapping();
  for (const mapped of mappedToHeaders) {
    const row = [];

    for (const header of headers) {
      row.push(withCellStyles(mapped[header], boldGrayBgLeftGrandTotal));
    }
    rows.push(row);
  }
  return rows;
};

const getMapping = (f: FundPerformanceOverview): object => {
  const body = {
    Entity: f.name,
    Vintage: getYearOrSpace(f.initialClosing),
    'Reporting period': f.selectedFP.period,
    'Total commitment': getCell({
      v: dashInsteadOfNullCommon(f.selectedFP.totalCommitment),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Contribution received': getCell({
      v: spaceInsteadOfNull(f.selectedFP.contribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: `D${currentIndexRow}-E${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV+Unf. Comm.': getCell({
      f: `F${currentIndexRow}+J${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    empty: null,

    'NAV & Distribution': getCell({
      v: spaceInsteadOfNull(f.beforeCarry.navAndDistribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    NAV: getCell({
      v: spaceInsteadOfNull(f.beforeCarry.navAndDistribution - f.beforeCarry.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    'Distribution inc. carry': getCell({
      v: spaceInsteadOfNull(f.beforeCarry.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    'Net gain/(loss)': getCell({
      v: f.beforeCarry.ngl,
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    TVPI: getCell({
      f: getTvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),

    DPI: getCell({
      f: getDpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),

    RVPI: getCell({
      f: getRvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),

    'Net IRR': getCell({
      v: dashInsteadOfNullCommon(f.selectedFP.netIrr ?? f.selectedFP.irrCalculated),
      z: formats.percents,
      t: 'n',
    }),
    'empty(1)': null,
    'Total carried interest': getCell({
      v: f.selectedFP.carryInterest,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Realized carried interest': getCell({
      v: f.selectedFP.carryDistribution,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unrealized carried interest': getCell({
      v: f.selectedFP.carryInterest - f.selectedFP.carryDistribution,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(2)': null,
    'NAV & Distribution(1)': getCell({
      v: spaceInsteadOfNull(f.afterCarry.navAndDistribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV(1)': getCell({
      v: spaceInsteadOfNull(f.afterCarry.navAndDistribution - f.afterCarry.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    Distribution: getCell({
      v: spaceInsteadOfNull(f.afterCarry.distribution),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)(1)': getCell({
      v: spaceInsteadOfNull(f.afterCarry.ngl),
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'TVPI(1)': getCell({
      f: getTvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'DPI(1)': getCell({
      f: getDpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'RVPI(1)': getCell({
      f: getRvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR(1)': getCell({
      v: dashInsteadOfNullCommon(f.selectedFP.netIrrAfterCarry ?? f.selectedFP.irrCalculatedAfterCarry),
      z: formats.percents,
      t: 'n',
    }),
  };
  currentIndexRow += 1;
  return body;
};

const getSubTotalMapping = (startIndex: number): any => {
  const body = {
    Entity: shouldUseSubtotal ? 'Sub total' : 'Total',
    Vintage: '',
    'Reporting period': '',
    'Total commitment': getCell({
      f: `SUM(D${startIndex}:D${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Contribution received': getCell({
      f: `SUM(E${startIndex}:E${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: `D${currentIndexRow}-E${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV+Unf. Comm.': getCell({
      f: `F${currentIndexRow}+J${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    empty: null,
    'NAV & Distribution': getCell({
      f: `SUM(I${startIndex}:I${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    NAV: getCell({
      f: `SUM(J${startIndex}:J${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Distribution inc. carry': getCell({
      f: `SUM(K${startIndex}:K${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)': getCell({
      f: `SUM(L${startIndex}:L${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    TVPI: getCell({
      f: getTvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    DPI: getCell({
      f: getDpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    RVPI: getCell({
      f: getRvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR': getCell({
      v: spaceInsteadOfNull(null),
      z: formats.multiplier,
      t: 'n',
    }),
    'empty(1)': null,
    'Total carried interest': getCell({
      f: `SUM(R${startIndex}:R${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Realized carried interest': getCell({
      f: `SUM(S${startIndex}:S${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unrealized carried interest': getCell({
      f: `SUM(T${startIndex}:T${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(2)': null,
    'NAV & Distribution(1)': getCell({
      f: `SUM(V${startIndex}:V${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV(1)': getCell({
      f: `SUM(W${startIndex}:W${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Distribution': getCell({
      f: `SUM(X${startIndex}:X${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)(1)': getCell({
      f: `SUM(Y${startIndex}:Y${currentIndexRow - 1})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'TVPI(1)': getCell({
      f: getTvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'DPI(1)': getCell({
      f: getDpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'RVPI(1)': getCell({
      f: getRvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR(1)': getCell({
      v: '',
      z: formats.percents,
      t: 'n',
    }),
    'empty(3)': null,
  };
  subTotalIndexes.push(currentIndexRow);
  return [body];
};

const getGrandTotalMapping = (): any => {
  const start = subTotalIndexes[0];
  const end = subTotalIndexes.length > 1 ? subTotalIndexes[1] : start;
  const body = {
    Entity: 'Grand total',
    Vintage: '',
    'Reporting period': '',
    'Total commitment': getCell({
      f: `SUM(D${start},D${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Contribution received': getCell({
      f: `SUM(E${start},E${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV+Unf. Comm.': getCell({
      f: `F${currentIndexRow}+J${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unfunded commitment': getCell({
      f: `D${currentIndexRow}-E${currentIndexRow}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    empty: null,
    'NAV & Distribution': getCell({
      f: `SUM(I${start},I${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    NAV: getCell({
      f: `SUM(J${start},J${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Distribution inc. carry': getCell({
      f: `SUM(K${start},K${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)': getCell({
      f: `SUM(L${start},L${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),

    TVPI: getCell({
      f: getTvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    DPI: getCell({
      f: getDpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    RVPI: getCell({
      f: getRvpiFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR': getCell({
      v: spaceInsteadOfNull(null),
      z: formats.multiplier,
      t: 'n',
    }),
    'empty(1)': null,
    'Total carried interest': getCell({
      f: `SUM(R${start},R${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Realized carried interest': getCell({
      f: `SUM(S${start},S${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Unrealized carried interest': getCell({
      f: `SUM(T${start},T${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty(2)': null,
    'NAV & Distribution(1)': getCell({
      f: `SUM(V${start},V${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'NAV(1)': getCell({
      f: `SUM(W${start},W${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Distribution': getCell({
      f: `SUM(X${start},X${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'Net gain/(loss)(1)': getCell({
      f: `SUM(Y${start},Y${end})`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'TVPI(1)': getCell({
      f: getTvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'DPI(1)': getCell({
      f: getDpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'RVPI(1)': getCell({
      f: getRvpiAfterCarryFormula(),
      z: formats.multiplier,
      t: 'n',
    }),
    'Net IRR(1)': getCell({
      v: '',
      z: formats.percents,
      t: 'n',
    }),
    'empty(3)': null,
  };
  return [body];
};

const getTvpiFormula = (): string => `=IFERROR((L${currentIndexRow} + E${currentIndexRow}) /E${currentIndexRow},0)`;
const getDpiFormula = (): string => `=IFERROR(K${currentIndexRow}/E${currentIndexRow},0)`;
const getRvpiFormula = (): string => `=IFERROR(J${currentIndexRow} /E${currentIndexRow},0)`;

const getTvpiAfterCarryFormula = (): string => `=IFERROR((Y${currentIndexRow} + E${currentIndexRow}) /E${currentIndexRow},0)`;
const getDpiAfterCarryFormula = (): string => `=IFERROR(X${currentIndexRow}/E${currentIndexRow},0)`;
const getRvpiAfterCarryFormula = (): string => `=IFERROR(W${currentIndexRow}/E${currentIndexRow},0)`;


const resetData = (): void => {
  currentIndexRow = 3;
  subTotalIndexes = [];
};

// row starts from 3
let currentIndexRow = 3;
let subTotalIndexes = [];
let shouldUseSubtotal: boolean;
